// src/modules/requerimiento/components/RequerimientoHeader.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Table, TableBody, TableCell, TableRow, CircularProgress, Alert } from "@mui/material";
import { getRequerimiento } from "../services/reqSolicitudes"; // ajusta la ruta si difiere

// Estados conocidos (agrega/ajusta según tu flujo real)
const estadoMap = {
  "1": { label: "Coordinación", color: "warning" },
  "2": { label: "Programación", color: "info" },
  "3": { label: "Trabajo de campo", color: "primary" },
  "4": { label: "Trabajo de campo", color: "primary" },
  "7": { label: "Revisión en Sistemas", color: "secondary" },
  "8": { label: "Finalizado", color: "success" },
};

// Fallback si no llega nombre_ot_tipo
const tipoMap = { "1": "Mantenimiento", "2": "Devolución", "3": "Adición" };

export default function RequerimientoHeader({ cod }) {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!cod) return;
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const { data } = await getRequerimiento(cod);
        if (!cancel) setReq(data);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.error || e.message || "Error al cargar el requerimiento");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [cod]);

  const estadoInfo = useMemo(() => {
    const key = String(req?.estado ?? "");
    return estadoMap[key] || null;
  }, [req?.estado]);

  const tipoLabel = useMemo(() => {
    // prioriza nombre legible si viene del backend
    return req?.nombre_ot_tipo || tipoMap[String(req?.tipo_req ?? "")] || "—";
  }, [req?.nombre_ot_tipo, req?.tipo_req]);

  const placasHeader = useMemo(() => {
    const raw = (req?.placa ?? "").toString().trim();
    if (!raw) return [];
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [req?.placa]);

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} /> Cargando requerimiento...
        </Box>
      )}

      {!loading && err && <Alert severity="error">{err}</Alert>}

      {!loading && !err && (
        <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 1 } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 160 }}>Código</TableCell>
              <TableCell>{cod || req?.cod || "—"}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 160 }}>Cliente</TableCell>
              <TableCell>{req?.razon_social ?? "—"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Tipo / Estado</TableCell>
              <TableCell>
                {tipoLabel}
                {estadoInfo && (
                  <span style={{ marginLeft: 8 }}>
                    <strong>•</strong> {estadoInfo.label}
                  </span>
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
              <TableCell>{req?.sucursal || "—"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Placas</TableCell>
              <TableCell colSpan={3}>
                {placasHeader.length > 0 ? placasHeader.join(", ") : "—"}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Observación</TableCell>
              <TableCell colSpan={3}>{req?.obs2 ?? req?.observacion ?? "—"}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
